import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    Button, 
    TextField, 
    Tabs, 
    Tab, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Checkbox,
    Chip,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import AddSymbolModal from './AddSymbolModal';
import EditSymbolModal from './EditSymbolModal';
import { addSymbol, updateSymbol, deleteSymbol, getAllSymbols, getSymbolsByLP } from '../../api/symbolApi';
import { 
    subscribeToSymbol, 
    unsubscribeFromSymbol, 
    subscribeToAllSymbols, 
    unsubscribeFromAllSymbols 
} from '../../api/symbolSubscriptionApi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../provider/authProvider';
import './SettingsResponsive.css';

// Prevent scroll wheel from changing numeric input values
const preventScrollOnNumberInputs = () => {
    const handleWheel = (e) => {
        if (e.target.type === 'number' && e.target.closest('.MuiTextField-root')) {
            e.preventDefault();
        }
    };

    // Add event listener to prevent scroll on number inputs
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    // Cleanup function
    return () => {
        document.removeEventListener('wheel', handleWheel);
    };
};

const SymbolManagement = ({ open, onClose, lp }) => {
    const { hasPermission } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddSymbol, setShowAddSymbol] = useState(false);
    const [showEditSymbol, setShowEditSymbol] = useState(false);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [symbols, setSymbols] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedSymbols, setSelectedSymbols] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Prevent scroll wheel from changing numeric input values
    useEffect(() => {
        const cleanup = preventScrollOnNumberInputs();
        return cleanup;
    }, []);

    // Fetch symbols from API for specific LP
    const fetchSymbols = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            if (!lp || !lp.id) {
                throw new Error('Liquidity Provider information is missing');
            }

            console.log('Fetching symbols for LP:', lp.id, lp.name);
            const data = await getSymbolsByLP(token, lp.id);
            // Handle API response structure - adjust based on actual response
            const symbolsArray = Array.isArray(data) ? data : (data?.data || []);
            setSymbols(symbolsArray);
            console.log('Fetched symbols for LP:', symbolsArray);
        } catch (error) {
            console.error('Error fetching symbols for LP:', error);
            setError(error.message || 'Failed to fetch symbols for this liquidity provider');
            toast.error('Failed to load symbols');
        } finally {
            setLoading(false);
        }
    };

    // Load symbols when modal opens
    React.useEffect(() => {
        if (open) {
            fetchSymbols();
        }
    }, [open]);

    const getTypeColor = (type) => {
        switch (type) {
            case 'Forex': return '#00cfff';
            case 'Metal': return '#ffd600';
            case 'Index': return '#1de9b6';
            case 'Crypto': return '#7b61ff';
            default: return '#bfc9db';
        }
    };

    const getStatusColor = (status) => {
        return status === 'Active' ? '#1de9b6' : '#ff6b6b';
    };

    const getSubscriptionColor = (isSubscribed) => {
        return isSubscribed ? '#1de9b6' : '#bfc9db';
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handle individual symbol subscription
    const handleSubscribe = async (symbol) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            // Update UI immediately (optimistic update)
            setSymbols(prevSymbols => 
                prevSymbols.map(s => 
                    s.id === symbol.id ? { ...s, isSubscribed: true } : s
                )
            );

            await subscribeToSymbol(token, symbol.id);
            toast.success(`Subscribed to ${symbol.name || symbol.symbol}`, {
                containerId: 'symbol-management-toasts'
            });
            // Dispatch custom event to notify Market Watch
            window.dispatchEvent(new CustomEvent('symbolSubscribed', { detail: symbol }));
        } catch (error) {
            console.error('Error subscribing to symbol:', error);
            // Revert the optimistic update on error
            setSymbols(prevSymbols => 
                prevSymbols.map(s => 
                    s.id === symbol.id ? { ...s, isSubscribed: false } : s
                )
            );
            toast.error('Failed to subscribe to symbol', {
                containerId: 'symbol-management-toasts'
            });
        }
    };

    const handleUnsubscribe = async (symbol) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            // Update UI immediately (optimistic update)
            setSymbols(prevSymbols => 
                prevSymbols.map(s => 
                    s.id === symbol.id ? { ...s, isSubscribed: false } : s
                )
            );

            await unsubscribeFromSymbol(token, symbol.id);
            toast.success(`Unsubscribed from ${symbol.name || symbol.symbol}`, {
                containerId: 'symbol-management-toasts'
            });
            // Dispatch custom event to notify Market Watch
            window.dispatchEvent(new CustomEvent('symbolUnsubscribed', { detail: symbol }));
        } catch (error) {
            console.error('Error unsubscribing from symbol:', error);
            // Revert the optimistic update on error
            setSymbols(prevSymbols => 
                prevSymbols.map(s => 
                    s.id === symbol.id ? { ...s, isSubscribed: true } : s
                )
            );
            toast.error('Failed to unsubscribe from symbol', {
                containerId: 'symbol-management-toasts'
            });
        }
    };

    // Handle bulk subscription actions
    const handleSubscribeAll = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            if (!lp || !lp.id) {
                toast.error('Liquidity Provider information is missing', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            // Update UI immediately (optimistic update)
            setSymbols(prevSymbols => 
                prevSymbols.map(s => ({ ...s, isSubscribed: true }))
            );

            await subscribeToAllSymbols(token, lp.id);
            toast.success('Subscribed to all symbols', {
                containerId: 'symbol-management-toasts'
            });
        } catch (error) {
            console.error('Error subscribing to all symbols:', error);
            // Revert the optimistic update on error
            fetchSymbols();
            toast.error('Failed to subscribe to all symbols', {
                containerId: 'symbol-management-toasts'
            });
        }
    };

    const handleUnsubscribeAll = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            if (!lp || !lp.id) {
                toast.error('Liquidity Provider information is missing', {
                    containerId: 'symbol-management-toasts'
                });
                return;
            }

            // Update UI immediately (optimistic update)
            setSymbols(prevSymbols => 
                prevSymbols.map(s => ({ ...s, isSubscribed: false }))
            );

            await unsubscribeFromAllSymbols(token, lp.id);
            toast.success('Unsubscribed from all symbols', {
                containerId: 'symbol-management-toasts'
            });
        } catch (error) {
            console.error('Error unsubscribing from all symbols:', error);
            // Revert the optimistic update on error
            fetchSymbols();
            toast.error('Failed to unsubscribe from all symbols', {
                containerId: 'symbol-management-toasts'
            });
        }
    };

    // Handle checkbox selection
    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        setSelectAll(isChecked);
        if (isChecked) {
            setSelectedSymbols(filteredSymbols.map(symbol => symbol.id));
        } else {
            setSelectedSymbols([]);
        }
    };

    const handleSelectSymbol = (event, symbolId) => {
        const isChecked = event.target.checked;
        if (isChecked) {
            setSelectedSymbols(prev => [...prev, symbolId]);
        } else {
            setSelectedSymbols(prev => prev.filter(id => id !== symbolId));
            setSelectAll(false);
        }
    };

    const handleAddSymbol = () => {
        setShowAddSymbol(true);
    };

    const handleEditSymbol = (symbol) => {
        console.log('SymbolManagement - handleEditSymbol called with:', symbol);
        console.log('SymbolManagement - Symbol ID:', symbol.id);
        console.log('SymbolManagement - Available fields:', Object.keys(symbol));
        setSelectedSymbol(symbol);
        setShowEditSymbol(true);
    };

    const handleDeleteSymbol = async (symbol) => {
        if (window.confirm(`Are you sure you want to delete "${symbol.symbol}"? This action cannot be undone.`)) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('Authentication token not found');
                    return;
                }

                await deleteSymbol(token, symbol.id);
                toast.success(`Symbol "${symbol.symbol}" deleted successfully!`);
                // Refresh symbols list
                fetchSymbols();
                // Dispatch custom event to notify Market Watch
                window.dispatchEvent(new CustomEvent('symbolDeleted', { detail: symbol }));
            } catch (error) {
                console.error('Error deleting symbol:', error);
                toast.error('Failed to delete symbol');
            }
        }
    };

    const handleAddSymbolSuccess = (newSymbol) => {
        console.log('Symbol added:', newSymbol);
        setShowAddSymbol(false);
        // Refresh symbols list
        fetchSymbols();
        // Dispatch custom event to notify Market Watch
        window.dispatchEvent(new CustomEvent('symbolAdded', { detail: newSymbol }));
    };

    const handleEditSymbolSuccess = (updatedSymbol) => {
        console.log('Symbol updated:', updatedSymbol);
        setShowEditSymbol(false);
        setSelectedSymbol(null);
        // Refresh symbols list
        fetchSymbols();
        // Dispatch custom event to notify Market Watch
        window.dispatchEvent(new CustomEvent('symbolUpdated', { detail: updatedSymbol }));
    };

    const filteredSymbols = symbols.filter(s => 
        (s.symbol && s.symbol.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.name && s.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.lpSymbolName && s.lpSymbolName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <>
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1000,
                maxHeight: '90vh',
                background: 'rgba(24, 31, 44, 0.95)',
                borderRadius: 2,
                border: '1px solid rgba(6, 182, 212, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SettingsIcon sx={{ color: '#00cfff', fontSize: 24 }} />
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                            Symbol Management - {lp?.name?.toUpperCase() || 'CENTROID'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip
                            label={`Total: ${symbols.length}`}
                            sx={{
                                background: '#00cfff',
                                color: '#000',
                                fontWeight: 600
                            }}
                        />
                        <Chip
                            label={`Subscribed: ${symbols.filter(s => s.isSubscribed === true).length}`}
                            sx={{
                                background: '#1de9b6',
                                color: '#000',
                                fontWeight: 600
                            }}
                        />
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#fff',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: '#bfc9db'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                color: '#bfc9db',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&.Mui-selected': {
                                    color: '#1de9b6'
                                }
                            },
                            '& .MuiTabs-indicator': {
                                background: '#1de9b6'
                            }
                        }}
                    >
                        <Tab label="Available Symbols" />
                        <Tab label="Subscribed Symbols" />
                        <Tab label="Symbol Mappings" />
                        <Tab label="Statistics" />
                    </Tabs>
                </Box>

                {/* Action Bar */}
                <Box sx={{
                    p: 2,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <TextField
                        placeholder="Search symbols..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{
                            width: 300,
                            '& .MuiInputLabel-root': { color: '#bfc9db' },
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {hasPermission('Settings.LPManagement.Symbols.Create') && (
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            size="small"
                            onClick={handleAddSymbol}
                            sx={{
                                background: '#ffd600',
                                color: '#000',
                                borderRadius: 1,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                    background: '#ffcc02'
                                }
                            }}
                        >
                            Add Symbol
                        </Button>
                        )}
                        
                        {/* Bulk action buttons - only show when all symbols are selected */}
                        {selectAll && filteredSymbols.length > 0 && (
                            <>
                                <Button
                                    startIcon={<CheckIcon />}
                                    variant="contained"
                                    size="small"
                                    onClick={handleSubscribeAll}
                                    sx={{
                                        background: '#1de9b6',
                                        color: '#000',
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: '#19b89b'
                                        }
                                    }}
                                >
                                    Subscribe All
                        </Button>
                        <Button
                                    startIcon={<CancelIcon />}
                                    variant="contained"
                            size="small"
                                    onClick={handleUnsubscribeAll}
                            sx={{
                                        background: '#ff6b6b',
                                        color: '#fff',
                                borderRadius: 1,
                                textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: '#ff5252'
                                        }
                            }}
                        >
                                    Unsubscribe All
                        </Button>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress sx={{ color: '#1de9b6' }} />
                        <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                            Loading symbols...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            m: 3,
                            background: 'rgba(211, 47, 47, 0.1)',
                            border: '1px solid #d32f2f',
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#ff6b6b' }
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Table */}
                {!loading && !error && (
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: 'calc(90vh - 200px)'
                }}>
                    <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                        <Checkbox 
                                            sx={{ color: '#bfc9db' }} 
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Symbol</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Name</TableCell>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Type</TableCell>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Status</TableCell>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Subscription</TableCell>
                                    <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                    {filteredSymbols.length > 0 ? (
                                        filteredSymbols.map((row) => (
                                            <TableRow key={row.id || row.symbol} sx={{ '&:hover': { background: 'rgba(6, 182, 212, 0.1)' } }}>
                                        <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Checkbox 
                                                        sx={{ color: '#bfc9db' }} 
                                                        checked={selectedSymbols.includes(row.id)}
                                                        onChange={(e) => handleSelectSymbol(e, row.id)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: '#fff', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    {row.symbol || row.name}
                                        </TableCell>
                                        <TableCell sx={{ color: '#fff', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    {row.name || row.symbol}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Chip
                                                        label={row.securityType || row.type || 'Unknown'}
                                                size="small"
                                                sx={{
                                                            background: getTypeColor(row.securityType || row.type),
                                                    color: '#000',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Chip
                                                        label={row.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                            background: getStatusColor(row.isActive ? 'Active' : 'Inactive'),
                                                    color: '#000',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)', textAlign: 'center' }}>
                                                    {row.isSubscribed ? (
                                                        <CheckIcon sx={{ color: '#1de9b6', fontSize: 24 }} />
                                                    ) : (
                                                        <CancelIcon sx={{ color: '#ff6b6b', fontSize: 24 }} />
                                                    )}
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        {row.isSubscribed ? (
                                                <Button
                                                    startIcon={<RemoveIcon />}
                                                    variant="contained"
                                                    size="small"
                                                                onClick={() => handleUnsubscribe(row)}
                                                    sx={{
                                                        background: '#ff6b6b',
                                                        color: '#fff',
                                                        borderRadius: 1,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        '&:hover': {
                                                            background: '#ff5252'
                                                        }
                                                    }}
                                                >
                                                    Unsubscribe
                                                </Button>
                                            ) : (
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    variant="contained"
                                                    size="small"
                                                                onClick={() => handleSubscribe(row)}
                                                    sx={{
                                                        background: '#1de9b6',
                                                        color: '#000',
                                                        borderRadius: 1,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        '&:hover': {
                                                            background: '#19b89b'
                                                        }
                                                    }}
                                                >
                                                    Subscribe
                                                </Button>
                                            )}
                                                        {hasPermission('Settings.LPManagement.Symbols.Edit') && (
                                                        <IconButton
                                                            onClick={() => handleEditSymbol(row)}
                                                            size="small"
                                                            sx={{
                                                                color: '#00cfff',
                                                                '&:hover': {
                                                                    background: 'rgba(0, 207, 255, 0.1)',
                                                                    color: '#1de9b6'
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        )}
                                                        {hasPermission('Settings.LPManagement.Symbols.Delete') && (
                                                        <IconButton
                                                            onClick={() => handleDeleteSymbol(row)}
                                                            size="small"
                                                            sx={{
                                                                color: '#ff6b6b',
                                                                '&:hover': {
                                                                    background: 'rgba(255, 107, 107, 0.1)',
                                                                    color: '#ff5252'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography variant="body1" sx={{ color: '#bfc9db' }}>
                                                    No symbols found
                                                </Typography>
                                        </TableCell>
                                    </TableRow>
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                )}

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={onClose}
                        sx={{
                            color: '#ff6b6b',
                            borderColor: '#ff6b6b',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={onClose}
                        sx={{
                            background: '#1de9b6',
                            color: '#000',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#19b89b'
                            }
                        }}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>
        </Modal>

            {/* Child Modals - Render outside of main modal */}
            <AddSymbolModal
                open={showAddSymbol}
                onClose={() => setShowAddSymbol(false)}
                onSuccess={handleAddSymbolSuccess}
                lpId={lp?.id}
            />

            <EditSymbolModal
                open={showEditSymbol}
                onClose={() => setShowEditSymbol(false)}
                onSuccess={handleEditSymbolSuccess}
                symbol={selectedSymbol}
            />
        </>
    );
};

export default SymbolManagement; 
